import { useState } from "react";
import { Table, Avatar, Typography, Tag, Row, Tooltip, Button } from "antd";
import { EditOutlined } from "@ant-design/icons";
import moment from "moment-timezone";
import { userTypes } from "../../types";
import { staffUserTypes } from "../../utils/constants";
import { GET_USERS, GET_LOCATIONS } from "./requests";
import { useQuery } from "@apollo/client";
import { Container } from "./elements";
import TableTitle from "./table-title";
import CreateUser from "./components/createModal";
import EditUser from "./components/editModal";

const { Text } = Typography;

const Users = () => {
  const [searchFilter, setSearchFilter] = useState<string>("");
  const [typeFilter, setTypeFilter] = useState<string>("");
  const [locationFilter, setLocationFilter] = useState<string | null>(null);
  const [locationSearch, setLocationSearch] = useState<string>("");
  const [showCreateModal, setShowCreateModal] = useState<boolean>(false);
  const [showEditModal, setShowEditModal] = useState<boolean>(false);
  const [userInfo, setUserInfo] = useState<typeof userTypes.user | undefined>();

  const [pageConfig, setPageConfig] = useState({
    page: 1,
    pageSize: 10,
  });

  const { data, loading, refetch } = useQuery(GET_USERS, {
    variables: {
      params: pageConfig,
      search: searchFilter || undefined,
      type: typeFilter || undefined,
      excludeTypes: ["client"],
      location: locationFilter ? { eq: locationFilter } : undefined,
    },
  });

  const { data: locationsData, loading: loadingLocations } = useQuery(GET_LOCATIONS, {
    variables: {
      params: { page: 1,
        pageSize: 50 },
      name: locationSearch || undefined,
    },
  });

  const users = data?.users?.results ?? [];

  const columns = [
    {
      title: "",
      dataIndex: "img",
      key: "img",
      width: "8%",
      render: (img: string | undefined) => (img ? <Avatar src={img} /> : null),
    },
    {
      title: "Nombre",
      key: "name",
      render: (row: typeof userTypes.user) => {
        const fullName = `${row.firstName} ${row.lastName}`;
        return (
          <div>
            <Text strong>{fullName}</Text>
            <br />
            <Text style={{ color: "grey" }}>{row?.email}</Text>
          </div>
        );
      },
    },
    {
      title: "Tipo",
      key: "type",
      render: (row: any) => (
        <div>
          <Text style={{ fontWeight: "800" }}>{staffUserTypes[row.type as keyof typeof staffUserTypes]?.name ?? row.type}</Text>
          <br />
          <Tag color="blue">{moment(row?.createdAt).format("lll")}</Tag>
        </div>
      ),
    },
    {
      title: "Franquicia",
      key: "name",
      dataIndex: "location",
      render: (row: any) => (
        <div>
          <Text strong>{row?.name}</Text>
          <br />
          <Text style={{ color: "#8c8c8c" }}>
            {`${row?.address.street}, ${row?.address.city}, ${row?.address.country}`}
          </Text>
        </div>
      ),
    },
    {
      title: "Acción",
      key: "action",
      render: (row: typeof userTypes.user) => {
        return (
          <Row>
            <Tooltip placement="top" title="Editar">
              <Button
                type="primary"
                icon={<EditOutlined style={{ color: "white" }} />}
                style={{ margin: 5 }}
                onClick={() => {
                  setShowEditModal(true);
                  setUserInfo(row);
                }}
              >
                Editar
              </Button>
            </Tooltip>
          </Row>
        );
      },
    },
  ];

  return (
    <Container style={{ overflow: "scroll" }}>
      <Table
        title={() => (
          <TableTitle
            setSearchFilter={setSearchFilter}
            setTypeFilter={setTypeFilter}
            setLocationFilter={setLocationFilter}
            setLocationSearch={setLocationSearch}
            locations={locationsData?.locations?.results ?? []}
            loadingLocations={loadingLocations}
            setShowCreateModal={setShowCreateModal}
          />
        )}
        dataSource={users}
        columns={columns}
        rowKey="id"
        loading={loading}
        pagination={{
          current: Number(pageConfig.page),
          defaultCurrent: pageConfig.page,
          pageSize: Number(pageConfig.pageSize),
          defaultPageSize: pageConfig.pageSize,
          total: data?.users?.info?.count,
          showTotal: (total) => <Text disabled>{`${total} resultados`}</Text>,
          onChange: (page, pageSize) => {
            setPageConfig({
              page,
              pageSize,
            });
          },
          showSizeChanger: true,
        }}
      />
      <CreateUser
        showModal={showCreateModal}
        setShowModal={setShowCreateModal}
        refetch={refetch}
        isLocationView={false}
        onUserCreated={() => {}}
      />
      <EditUser
        showModal={showEditModal}
        setShowModal={setShowEditModal}
        refetch={refetch}
        user={userInfo}
      />
    </Container>
  );
};

export default Users;
